import { call, delay, put, select, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import {
  PartnerRoleType,
  RolePermissionDetailType,
  UserListType,
  UserDetailType,
  UserRolePermissionsType,
} from "../../slices/user/Types";
import {
  createRolePayloadType,
  editUserRolePermissionsPayloadType,
  deleteRoleSagaAction,
  createRoleSagaAction,
  updateRoleSagaAction,
  updateRolePayloadType,
  getRolePermissionDetailSagaAction,
  upsertRolePermissionsSagaAction,
  getUserListHIDSagaAction,
  getUsersByTypeSagaAction,
  getUserListSagaAction,
  getUserRolePermissionsSagaAction,
  UserListRefreshTarget,
  getPartnerRolesSagaAction,
  getHidRolesSagaAction,
  userSagaActionTypes,
  sendAccountInviteSagaAction,
} from "./saga-actions";
import { addUserPayloadType, updateUserPayloadType } from "./saga-actions";
import { RootStateType } from "../..";
import { userSliceAction } from "../../slices/user";
import { authSliceAction } from "../../slices/auth";
import { getLoggedUserDetailSagaAction } from "../auth/saga-actions";

type getUserListAPIResponseType = {
  success: boolean;
  message: string;
  data: UserListType;
};
const getUserListAPI = async (partnerId: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getUserListAPIResponseType>>(
      "/user/getUser/?partner_id=" + partnerId
    )
    .then((res) => res.data);
  return response;
};

type getUserDetailAPIResponseType = {
  success: boolean;
  message: string;
  data: UserDetailType;
};
const getUserDetailAPI = async (ID: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<getUserDetailAPIResponseType>>("/user/getUser?ID=" + ID)
    .then((res) => res.data);
  return response;
};

type addUserAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const addUserAPI = async (data: addUserPayloadType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<addUserAPIResponseType>>("/user/insertUser/", data)
    .then((res) => res.data);
  return response;
};

type updateUserAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const updateUserAPI = async (user: updateUserPayloadType) => {
  let response = axiosRestInstance
    .put<AxiosResponse<updateUserAPIResponseType>>(
      "/user/updateUser/" + user.ID,
      user
    )
    .then((res) => res.data);
  return response;
};

type deleteUserAPIResponseType = {
  success: boolean;
  message: string;
  data: any;
};
const deleteUserAPI = async (userID: number) => {
  let response = axiosRestInstance
    .delete<AxiosResponse<deleteUserAPIResponseType>>("/user/" + userID)
    .then((res) => res.data);
  return response;
};

type getUserListHIDAPIResponseType = {
  success: boolean;
  message: string;
  data: UserListType;
};
const getUserListHIDAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getUserListHIDAPIResponseType>>("/user/getUser/?type=HID")
    .then((res) => res.data);
  return response;
};

const getUsersByTypeAPI = async (userType: "HID" | "partner") => {
  let response = axiosRestInstance
    .get<AxiosResponse<getUserListHIDAPIResponseType>>(
      "/user/getUser/?type=" + userType
    )
    .then((res) => res.data);
  return response;
};

function* refreshUserList(target: UserListRefreshTarget, partnerId?: number) {
  if (target === "hid") {
    yield put(getUsersByTypeSagaAction("HID"));
  } else if (target === "partner-global") {
    yield put(getUsersByTypeSagaAction("partner"));
  } else if (target === "partner-org" && partnerId) {
    yield put(getUserListSagaAction(partnerId));
  }
}

function resolveListRefreshTarget(
  payload: addUserPayloadType & { listRefreshTarget?: UserListRefreshTarget }
): UserListRefreshTarget {
  if (payload.listRefreshTarget) return payload.listRefreshTarget;
  if (payload.partner) return "partner-org";
  return "hid";
}

type getUserRolePermissionsAPIResponseType =
  | {
      success: boolean;
      message: string;
      data: UserRolePermissionsType;
    }
  | UserRolePermissionsType
  | Array<Record<string, unknown>>;
const getUserRolePermissionsAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getUserRolePermissionsAPIResponseType>>(
      "/user/getRole"
    )
    .then((res) => res.data);
  return response;
};

type getPartnerRolesAPIResponseType =
  | PartnerRoleType[]
  | { success?: boolean; data?: PartnerRoleType[] };

const getPartnerRolesAPI = async () => {
  const response = await axiosRestInstance
    .get<AxiosResponse<getPartnerRolesAPIResponseType>>(
      "/user/getRole/?type=partner"
    )
    .then((res) => res.data);
  return response;
};

const normalizePartnerRoles = (
  response: getPartnerRolesAPIResponseType
): PartnerRoleType[] => {
  if (Array.isArray(response)) return response;
  if (response && typeof response === "object" && Array.isArray(response.data)) {
    return response.data;
  }
  return [];
};

const isHidAssignableRoleType = (type: unknown): boolean => {
  const normalized = String(type ?? "").trim().toLowerCase();
  return normalized === "hid" || normalized === "super admin";
};

const filterHidAssignableRoles = (roles: PartnerRoleType[]): PartnerRoleType[] =>
  roles
    .filter((role) => Number(role.status) === 1)
    .filter((role) => isHidAssignableRoleType(role.type))
    .sort((a, b) => a.name.localeCompare(b.name));

type editUserRolePermissionsAPIResponseType = {
  success: boolean;
  message: string;
};
const editUserRolePermissionsAPI = async (
  payload: editUserRolePermissionsPayloadType
) => {
  let response = axiosRestInstance
    .put<AxiosResponse<editUserRolePermissionsAPIResponseType>>(
      "/user/update-permissions",
      payload
    )
    .then((res) => res.data);
  return response;
};

type toggleUserEnableResponseType = {
  success: boolean;
  message: string;
};
const toggleUserEnableAPI = async (payload: {
  ID: number;
  userEmail: string;
  enable: number;
}) => {
  let userEmail = payload.userEmail.replace("+", "%2B");
  let response = axiosRestInstance
    .put<AxiosResponse<toggleUserEnableResponseType>>(
      "/user/userStatus?userEmail=" + userEmail,
      { enable: payload.enable }
    )
    .then((res) => res.data);
  return response;
};

type sendAccountInviteResponseType = {
  success: boolean;
  message: string;
};
const sendAccountInviteAPI = async (ID: number) => {
  let response = axiosRestInstance
    .put<AxiosResponse<sendAccountInviteResponseType>>("/user/invite/" + ID)
    .then((res) => res.data);
  return response;
};

type deleteRoleResponseType = {
  success: boolean;
  message: string;
  data?: any;
};
const deleteRoleAPI = async (ID: number) => {
  let response = axiosRestInstance
    .delete<AxiosResponse<deleteRoleResponseType>>("/user/delete/" + ID)
    .then((res) => res.data);
  return response;
};

type createRoleResponseType = {
  success: boolean;
  message: string;
  data?: any;
};
const createRoleAPI = async (payload: {
  name: string;
  type: string;
  description: string;
  clone_from_role_id?: number;
}) => {
  let response = axiosRestInstance
    .post<AxiosResponse<createRoleResponseType>>("/user/createRole", payload)
    .then((res) => res.data);
  return response;
};

type updateRoleResponseType = {
  success: boolean;
  message: string;
  data?: any;
};
const updateRoleAPI = async (
  payload: Omit<updateRolePayloadType, "onSuccess" | "onError">
) => {
  const { ID, ...body } = payload;
  let response = axiosRestInstance
    .put<AxiosResponse<updateRoleResponseType>>("/user/updateRole/" + ID, body)
    .then((res) => res.data);
  return response;
};

const getRolePermissionDetailAPI = async (roleID: number) => {
  let response = axiosRestInstance
    .get<AxiosResponse<RolePermissionDetailType>>("/permission/get/" + roleID)
    .then((res) => res.data);
  return response;
};

type upsertRolePermissionsResponseType = {
  success: boolean;
  message: string;
};
const upsertRolePermissionsAPI = async (payload: {
  role_id: number;
  permissions: Array<{ permission_id: number; action: string }>;
}) => {
  let response = axiosRestInstance
    .put<AxiosResponse<upsertRolePermissionsResponseType>>(
      "/permission/upsert",
      payload
    )
    .then((res) => res.data);
  return response;
};

// API ABOVE --- SAGA BELOW

function* getUserListSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    yield put(userSliceAction.setUserListLoading(true));
    let response: getUserListAPIResponseType = yield call(
      getUserListAPI,
      action.payload.ID
    );
    const list = response.data ? response.data.reverse() : [];
    yield put(userSliceAction.setUserList(list));
    yield put(userSliceAction.setUserListLoading(false));
    yield put(userSliceAction.setFetchedUserListPartnerID(action.payload.ID));
  } catch (e) {
    yield put(userSliceAction.setUserList([]));
    yield put(userSliceAction.setUserListLoading(false));
    // Mark as fetched even on failure (e.g. 404 when partner has no users) so
    // components don't keep re-dispatching this request in a loop.
    yield put(userSliceAction.setFetchedUserListPartnerID(action.payload.ID));
    console.log(e);
  }
}

function* getUserDetailSaga(action: {
  type: string;
  payload: {
    ID: number;
  };
}) {
  try {
    let response: getUserDetailAPIResponseType = yield call(
      getUserDetailAPI,
      action.payload.ID
    );
    yield put(userSliceAction.setUserDetail(response.data));
  } catch (e) {
    console.log(e);
  }
}

const getUserApiResponseMessage = (
  response?: { message?: string; error?: string },
  fallback = "Something went wrong!"
) => response?.error || response?.message || fallback;

const getUserRequestErrorMessage = (
  e: any,
  fallback = "Something went wrong!"
) =>
  e?.response?.data?.error ||
  e?.response?.data?.message ||
  fallback;

function* addUserSaga(action: {
  type: string;
  payload: addUserPayloadType & { listRefreshTarget?: UserListRefreshTarget };
}) {
  const { listRefreshTarget, ...apiPayload } = action.payload;
  const refreshTarget = listRefreshTarget ?? resolveListRefreshTarget(action.payload);
  try {
    yield put(userSliceAction.setAddUserLoading(true));
    let response: addUserAPIResponseType = yield call(addUserAPI, apiPayload);
    yield put(userSliceAction.setAddUserLoading(false));
    if (response.success) {
      yield put(userSliceAction.setAddUserMessage(""));
      yield put(userSliceAction.setAddUserStatus(true));
      yield call(
        refreshUserList,
        refreshTarget,
        action.payload.partner
      );
    } else {
      yield put(
        userSliceAction.setAddUserMessage(
          getUserApiResponseMessage(response)
        )
      );
      yield put(userSliceAction.setAddUserStatus(false));
    }
  } catch (e: any) {
    yield put(
      userSliceAction.setAddUserMessage(getUserRequestErrorMessage(e))
    );
    yield put(userSliceAction.setAddUserLoading(false));
    yield put(userSliceAction.setAddUserStatus(false));
    console.log(e);
  }
}

function* updateUserSaga(action: {
  type: string;
  payload: {
    user: updateUserPayloadType;
    listRefreshTarget?: UserListRefreshTarget;
  };
}) {
  const refreshTarget =
    action.payload.listRefreshTarget ??
    (action.payload.user.partner ? "partner-org" : "hid");
  try {
    yield put(userSliceAction.setUpdateUserLoading(true));
    let response: updateUserAPIResponseType = yield call(
      updateUserAPI,
      action.payload.user
    );
    yield put(userSliceAction.setUpdateUserLoading(false));
    if (response.success) {
      yield put(userSliceAction.setUpdateUserMessage(""));
      yield put(userSliceAction.setUpdateUserStatus(true));
      yield call(
        refreshUserList,
        refreshTarget,
        action.payload.user.partner
      );
    } else {
      yield put(
        userSliceAction.setUpdateUserMessage(
          getUserApiResponseMessage(response)
        )
      );
      yield put(userSliceAction.setUpdateUserStatus(false));
    }
  } catch (e: any) {
    yield put(
      userSliceAction.setUpdateUserMessage(getUserRequestErrorMessage(e))
    );
    yield put(userSliceAction.setUpdateUserLoading(false));
    yield put(userSliceAction.setUpdateUserStatus(false));
    console.log(e);
  }
}

function* deleteUserSaga(action: {
  type: string;
  payload: {
    data: {
      userID: number;
      setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
      listType: "partner" | "hid" | "partner-global";
    };
  };
}) {
  try {
    yield put(userSliceAction.setDeleteUserLoading(true));
    let response: deleteUserAPIResponseType = yield call(
      deleteUserAPI,
      action.payload.data.userID
    );
    yield put(userSliceAction.setDeleteUserLoading(false));
    if (response.success) {
      const selectUserList = (state: RootStateType) => state.user;
      const { userList, userListHID, userListPartnerGlobal } = yield select(
        selectUserList
      );

      if (action.payload.data.listType === "partner") {
        const updatedUserList = userList.filter((user: any) => {
          return user.ID !== action.payload.data.userID;
        });
        yield put(userSliceAction.setUserList(updatedUserList));
      } else if (action.payload.data.listType === "partner-global") {
        const updatedList = userListPartnerGlobal.filter((user: any) => {
          return user.ID !== action.payload.data.userID;
        });
        yield put(userSliceAction.setUserListPartnerGlobal(updatedList));
      } else {
        const updatedUserListHID = userListHID.filter((user: any) => {
          return user.ID !== action.payload.data.userID;
        });
        yield put(userSliceAction.setUserListHID(updatedUserListHID));
      }

      yield put(userSliceAction.setDeleteUserMessage(
        getUserApiResponseMessage(response, "User deleted successfully")
      ));
      yield put(userSliceAction.setDeleteUserStatus("success"));
    } else {
      yield put(
        userSliceAction.setDeleteUserMessage(getUserApiResponseMessage(response))
      );
      yield put(userSliceAction.setDeleteUserStatus("error"));
    }
  } catch (e: any) {
    yield put(
      userSliceAction.setDeleteUserMessage(getUserRequestErrorMessage(e))
    );
    yield put(userSliceAction.setDeleteUserLoading(false));
    yield put(userSliceAction.setDeleteUserStatus("error"));
    console.log(e);
  }
}

function* getUserListHIDSaga(action: { type: string }) {
  yield put(getUsersByTypeSagaAction("HID"));
}

function* getUsersByTypeSaga(action: ReturnType<typeof getUsersByTypeSagaAction>) {
  const { userType } = action.payload;
  const isHid = userType === "HID";
  try {
    if (isHid) {
      yield put(userSliceAction.setUserListHIDLoading(true));
    } else {
      yield put(userSliceAction.setUserListPartnerGlobalLoading(true));
    }
    let response: getUserListHIDAPIResponseType = yield call(
      getUsersByTypeAPI,
      userType
    );
    const list = response.data ? [...response.data].reverse() : [];
    if (isHid) {
      yield put(userSliceAction.setUserListHID(list));
      yield put(userSliceAction.setUserListHIDLoading(false));
    } else {
      yield put(userSliceAction.setUserListPartnerGlobal(list));
      yield put(userSliceAction.setUserListPartnerGlobalLoading(false));
    }
  } catch (e) {
    if (isHid) {
      yield put(userSliceAction.setUserListHID([]));
      yield put(userSliceAction.setUserListHIDLoading(false));
    } else {
      yield put(userSliceAction.setUserListPartnerGlobal([]));
      yield put(userSliceAction.setUserListPartnerGlobalLoading(false));
    }
    console.log(e);
  }
}

function* updateUserMyAccountSaga(action: {
  type: string;
  payload: {
    user: updateUserPayloadType;
  };
}) {
  try {
    yield put(userSliceAction.setUpdateUserLoading(true));
    let response: updateUserAPIResponseType = yield call(
      updateUserAPI,
      action.payload.user
    );
    let updatedUserResponse: getUserDetailAPIResponseType = yield call(
      getUserDetailAPI,
      action.payload.user.ID
    );
    yield put(authSliceAction.setLoggedUserDetail(updatedUserResponse.data));
    yield put(userSliceAction.setUpdateUserLoading(false));
    yield put(userSliceAction.setUpdateUserStatus(true));
  } catch (e) {
    yield put(userSliceAction.setUpdateUserLoading(false));
    yield put(userSliceAction.setUpdateUserStatus(false));
    console.log(e);
  }
}

function* getUserRolePermissionsSaga(action: { type: string }) {
  try {
    yield put(userSliceAction.setUserRolePermissionsLoading(true));
    let response: getUserRolePermissionsAPIResponseType = yield call(
      getUserRolePermissionsAPI
    );
    const normalizedData =
      response &&
      typeof response === "object" &&
      !Array.isArray(response) &&
      "data" in response
        ? (response as { data: UserRolePermissionsType }).data
        : response;
    yield put(userSliceAction.setUserRolePermissions(normalizedData));
    yield put(userSliceAction.setUserRolePermissionsLoading(false));
  } catch (e) {
    yield put(userSliceAction.setUserRolePermissionsLoading(false));
    console.log(e);
  }
}

function* getPartnerRolesSaga() {
  try {
    yield put(userSliceAction.setPartnerRolesLoading(true));
    const response: getPartnerRolesAPIResponseType = yield call(
      getPartnerRolesAPI
    );
    const roles = normalizePartnerRoles(response).filter(
      (role) => Number(role.status) === 1
    );
    yield put(userSliceAction.setPartnerRoles(roles));
    yield put(userSliceAction.setPartnerRolesLoading(false));
  } catch (e) {
    yield put(userSliceAction.setPartnerRolesLoading(false));
    console.log(e);
  }
}

function* getHidRolesSaga() {
  try {
    yield put(userSliceAction.setHidRolesLoading(true));
    const response: getPartnerRolesAPIResponseType = yield call(
      getUserRolePermissionsAPI
    );
    const roles = filterHidAssignableRoles(normalizePartnerRoles(response));
    yield put(userSliceAction.setHidRoles(roles));
    yield put(userSliceAction.setHidRolesLoading(false));
  } catch (e) {
    yield put(userSliceAction.setHidRoles([]));
    yield put(userSliceAction.setHidRolesLoading(false));
    console.log(e);
  }
}

function* editUserRolePermissionsSaga(action: {
  type: string;
  payload: editUserRolePermissionsPayloadType;
}) {
  try {
    yield put(userSliceAction.setEditUserRolePermissionsLoading(true));
    let response: editUserRolePermissionsAPIResponseType = yield call(
      editUserRolePermissionsAPI,
      action.payload
    );
    yield put(userSliceAction.setEditUserRolePermissionsStatus(true));
    yield put(userSliceAction.setEditUserRolePermissionsLoading(false));
    yield put(getUserRolePermissionsSagaAction());
  } catch (e) {
    yield put(userSliceAction.setEditUserRolePermissionsStatus(false));
    yield put(userSliceAction.setEditUserRolePermissionsLoading(false));
    console.log(e);
  }
}

function* toggleUserEnableSaga(action: {
  type: string;
  payload: {
    ID: number;
    userEmail: string;
    enable: number;
  };
}) {
  try {
    yield put(userSliceAction.setToggleUserEnableID(action.payload.ID));
    yield put(userSliceAction.setToggleUserEnableLoading(true));
    let response: toggleUserEnableResponseType = yield call(
      toggleUserEnableAPI,
      action.payload
    );
    const selectUser = (state: RootStateType) => state.user;
    const { userList } = yield select(selectUser);
    const updatedUserList = userList.map((user: UserListType[0]) => {
      if (user.ID === action.payload.ID)
        return {
          ...user,
          status: action.payload.enable ? "Enable" : "Disable",
        };
      else return user;
    });
    yield put(userSliceAction.setUserList(updatedUserList));
    yield put(userSliceAction.setToggleUserEnableLoading(false));
  } catch (e) {
    yield put(userSliceAction.setToggleUserEnableLoading(false));
    console.log(e);
  }
}

function* sendAccountInviteSaga(action: {
  type: string;
  payload: {
    ID: number;
    userEmail: string;
  };
}) {
  try {
    yield put(userSliceAction.setSendAccountInviteLoading(true));
    let response: sendAccountInviteResponseType = yield call(
      sendAccountInviteAPI,
      action.payload.ID
    );
    
    if (response.success) {
      // Show success notification with email instead of ID
      const successMessage = `User with email ${action.payload.userEmail} successfully invited`;
      yield put(userSliceAction.setSendAccountInviteMessage(successMessage));
      yield put(userSliceAction.setSendAccountInviteStatus("success"));
    }
    
    yield put(userSliceAction.setSendAccountInviteLoading(false));
  } catch (e: any) {
    yield put(userSliceAction.setSendAccountInviteLoading(false));
    yield put(userSliceAction.setSendAccountInviteStatus("error"));
    yield put(userSliceAction.setSendAccountInviteMessage("Something went wrong"));
    console.log(e);
  }
}

function* deleteRoleSaga(action: ReturnType<typeof deleteRoleSagaAction>) {
  try {
    const response: deleteRoleResponseType = yield call(
      deleteRoleAPI,
      action.payload.ID
    );
    yield put(getUserRolePermissionsSagaAction());
    action.payload.onSuccess?.(response?.message || "Role successfully deleted.");
  } catch (e: any) {
    const errorMessage =
      e?.response?.data?.message || "Failed to delete role. Please try again.";
    action.payload.onError?.(errorMessage);
    console.log(e);
  }
}

function* createRoleSaga(action: ReturnType<typeof createRoleSagaAction>) {
  try {
    const payload: createRolePayloadType = action.payload;
    const requestPayload = {
      name: payload.name,
      type: payload.type,
      description: payload.description,
      ...(typeof payload.clone_from_role_id === "number"
        ? { clone_from_role_id: payload.clone_from_role_id }
        : {}),
    };
    const response: createRoleResponseType = yield call(
      createRoleAPI,
      requestPayload
    );
    yield put(getUserRolePermissionsSagaAction());
    payload.onSuccess?.(response?.message || "Role successfully added.");
  } catch (e: any) {
    const errorMessage =
      e?.response?.data?.message || "Failed to create role. Please try again.";
    action.payload.onError?.(errorMessage);
    console.log(e);
  }
}

function* updateRoleSaga(action: ReturnType<typeof updateRoleSagaAction>) {
  try {
    const payload: updateRolePayloadType = action.payload;
    const requestPayload = {
      ID: payload.ID,
      name: payload.name,
      type: payload.type,
      description: payload.description,
      status: payload.status,
      ...(typeof payload.clone_from_role_id === "number"
        ? { clone_from_role_id: payload.clone_from_role_id }
        : {}),
    };
    const response: updateRoleResponseType = yield call(
      updateRoleAPI,
      requestPayload
    );
    yield put(getUserRolePermissionsSagaAction());
    payload.onSuccess?.(response?.message || "Role successfully updated.");
  } catch (e: any) {
    const errorMessage =
      e?.response?.data?.message || "Failed to update role. Please try again.";
    action.payload.onError?.(errorMessage);
    console.log(e);
  }
}

function* getRolePermissionDetailSaga(
  action: ReturnType<typeof getRolePermissionDetailSagaAction>
) {
  try {
    yield put(userSliceAction.setRolePermissionDetailLoading(true));
    const response: RolePermissionDetailType = yield call(
      getRolePermissionDetailAPI,
      action.payload.roleID
    );
    yield put(userSliceAction.setRolePermissionDetail(response));
    yield put(userSliceAction.setRolePermissionDetailLoading(false));
  } catch (e) {
    yield put(userSliceAction.setRolePermissionDetail(null));
    yield put(userSliceAction.setRolePermissionDetailLoading(false));
    console.log(e);
  }
}

function* upsertRolePermissionsSaga(
  action: ReturnType<typeof upsertRolePermissionsSagaAction>
) {
  try {
    const response: upsertRolePermissionsResponseType = yield call(
      upsertRolePermissionsAPI,
      {
        role_id: action.payload.role_id,
        permissions: action.payload.permissions,
      }
    );
    yield put(
      getRolePermissionDetailSagaAction({ roleID: action.payload.role_id })
    );

    const selectLoggedUserRoleId = (state: RootStateType) =>
      state.auth.loggedUserDetail?.role_id;
    const loggedInRoleId: number | undefined = yield select(selectLoggedUserRoleId);

    // If current user's role was edited, refresh in-session permissions immediately.
    if (loggedInRoleId && loggedInRoleId === action.payload.role_id) {
      yield put(getLoggedUserDetailSagaAction());
    }
    action.payload.onSuccess?.(
      response?.message || "Permissions updated successfully."
    );
  } catch (e: any) {
    const errorMessage =
      e?.response?.data?.message || "Failed to update permissions.";
    action.payload.onError?.(errorMessage);
    console.log(e);
  }
}

function* userSaga() {
  yield takeLatest(userSagaActionTypes.callGetUserListSaga, getUserListSaga);
  yield takeLatest(
    userSagaActionTypes.callGetUserDetailSaga,
    getUserDetailSaga
  );
  yield takeLatest(userSagaActionTypes.callAddUserSaga, addUserSaga);
  yield takeLatest(userSagaActionTypes.callUpdateUserSaga, updateUserSaga);
  yield takeLatest(userSagaActionTypes.callDeleteUserSaga, deleteUserSaga);
  yield takeLatest(
    userSagaActionTypes.callGetUserListHIDSaga,
    getUserListHIDSaga
  );
  yield takeLatest(
    userSagaActionTypes.callGetUsersByTypeSaga,
    getUsersByTypeSaga
  );
  yield takeLatest(
    userSagaActionTypes.callUpdateUserMyAccountSaga,
    updateUserMyAccountSaga
  );
  yield takeLatest(
    userSagaActionTypes.callGetUserRolePermissionsSaga,
    getUserRolePermissionsSaga
  );
  yield takeLatest(
    userSagaActionTypes.callGetPartnerRolesSaga,
    getPartnerRolesSaga
  );
  yield takeLatest(userSagaActionTypes.callGetHidRolesSaga, getHidRolesSaga);
  yield takeLatest(
    userSagaActionTypes.callToggleUserEnableSaga,
    toggleUserEnableSaga
  );
  yield takeLatest(
    userSagaActionTypes.callEditUserRolePermissionsSaga,
    editUserRolePermissionsSaga
  );
  yield takeLatest(
    userSagaActionTypes.callSendAccountInviteSaga,
    sendAccountInviteSaga
  );
  yield takeLatest(userSagaActionTypes.callDeleteRoleSaga, deleteRoleSaga);
  yield takeLatest(userSagaActionTypes.callCreateRoleSaga, createRoleSaga);
  yield takeLatest(userSagaActionTypes.callUpdateRoleSaga, updateRoleSaga);
  yield takeLatest(
    userSagaActionTypes.callGetRolePermissionDetailSaga,
    getRolePermissionDetailSaga
  );
  yield takeLatest(
    userSagaActionTypes.callUpsertRolePermissionsSaga,
    upsertRolePermissionsSaga
  );
}

export default userSaga;
